import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { trackMiniAppEvent } from '../../../utils/analytics';
import {
  GUIDED_HINT_INACTIVITY_MS,
  hintEntryAtLevel,
  normalizeGuidedHints,
} from './guidedHintModel';

function useNarrowViewport(breakpointPx = 640) {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(`(max-width: ${breakpointPx}px)`).matches : false,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia(`(max-width: ${breakpointPx}px)`);
    const on = () => setNarrow(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [breakpointPx]);

  return narrow;
}

/**
 * Shared guided hint controller for activity engines.
 *
 * @param {object} opts
 * @param {string} opts.activityId
 * @param {unknown} opts.hints - JSON `hints` array (strings or { text, audioText?, level? })
 * @param {{ text: string, audioText?: string } | null | undefined} [opts.oneShotInteractionHint] — “how to tap/drag” shown at most once per session; then only content `hints` advance.
 * @param {string} [opts.explanation] - optional activity-level explanation
 * @param {() => string | null | undefined} [opts.getExplanation] - per-question / dynamic explanation
 * @param {boolean} [opts.enabled]
 * @param {boolean} [opts.isActivityComplete] - disables idle timer
 */
export function useGuidedHints({
  activityId,
  hints: rawHints,
  oneShotInteractionHint = null,
  explanation: explanationProp,
  getExplanation,
  enabled = true,
  isActivityComplete = false,
}) {
  const isNarrow = useNarrowViewport(640);
  const normalized = useMemo(() => normalizeGuidedHints(rawHints), [rawHints]);
  const maxLevel = normalized.length;

  const hasOneShot =
    !!oneShotInteractionHint &&
    typeof oneShotInteractionHint.text === 'string' &&
    oneShotInteractionHint.text.trim().length > 0;

  const [open, setOpen] = useState(false);
  const [visibleLevel, setVisibleLevel] = useState(1);
  const [wrongCount, setWrongCount] = useState(0);
  const [explanationOpen, setExplanationOpen] = useState(false);
  const [interactionConsumed, setInteractionConsumed] = useState(false);
  const interactionConsumedRef = useRef(false);

  const idleTimerRef = useRef(null);
  const lastShownKeyRef = useRef('');
  const prevWrongRef = useRef(0);

  const resolvedExplanation = useMemo(() => {
    const fromGetter = getExplanation?.();
    if (typeof fromGetter === 'string' && fromGetter.trim()) return fromGetter.trim();
    if (typeof explanationProp === 'string' && explanationProp.trim()) return explanationProp.trim();
    return '';
  }, [getExplanation, explanationProp]);

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current != null) {
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const armIdleTimer = useCallback(() => {
    clearIdleTimer();
    if (!enabled || isActivityComplete) return;
    idleTimerRef.current = window.setTimeout(() => {
      idleTimerRef.current = null;
      if (!enabled || isActivityComplete) return;
      const level = wrongCount > 0 ? Math.min(maxLevel, wrongCount) : 1;
      setVisibleLevel((vl) => Math.min(maxLevel, Math.max(vl, level)));
      setOpen(true);
      const key = `idle-${level}`;
      if (lastShownKeyRef.current !== key) {
        lastShownKeyRef.current = key;
        trackMiniAppEvent.hintShown(activityId, {
          trigger: 'idle',
          level,
          phase: hasOneShot && !interactionConsumedRef.current ? 'interaction' : 'content',
        });
      }
    }, GUIDED_HINT_INACTIVITY_MS);
  }, [activityId, clearIdleTimer, enabled, hasOneShot, isActivityComplete, maxLevel, wrongCount]);

  useEffect(() => {
    if (!enabled || isActivityComplete) {
      clearIdleTimer();
      return;
    }
    armIdleTimer();
    return () => clearIdleTimer();
  }, [armIdleTimer, clearIdleTimer, enabled, isActivityComplete]);

  useEffect(() => {
    if (wrongCount === 0) {
      prevWrongRef.current = 0;
      return;
    }
    if (prevWrongRef.current === wrongCount) return;
    prevWrongRef.current = wrongCount;
    const level = Math.min(maxLevel, wrongCount);
    setVisibleLevel((vl) => Math.min(maxLevel, Math.max(vl, level)));
    setOpen(true);
    const key = `wrong-${wrongCount}`;
    if (lastShownKeyRef.current !== key) {
      lastShownKeyRef.current = key;
      trackMiniAppEvent.hintShown(activityId, {
        trigger: 'incorrect',
        level,
        wrongCount,
        phase: hasOneShot && !interactionConsumedRef.current ? 'interaction' : 'content',
      });
    }
  }, [wrongCount, maxLevel, activityId, hasOneShot]);

  const noteUserAction = useCallback(() => {
    if (!enabled) return;
    armIdleTimer();
  }, [armIdleTimer, enabled]);

  const resetSession = useCallback(() => {
    clearIdleTimer();
    prevWrongRef.current = 0;
    interactionConsumedRef.current = false;
    setInteractionConsumed(false);
    setOpen(false);
    setVisibleLevel(1);
    setWrongCount(0);
    setExplanationOpen(false);
    lastShownKeyRef.current = '';
    if (!isActivityComplete) armIdleTimer();
  }, [armIdleTimer, clearIdleTimer, isActivityComplete]);

  const noteCorrectAnswer = useCallback(() => {
    if (!enabled) return;
    setWrongCount(0);
    setExplanationOpen(false);
    setOpen(false);
    setVisibleLevel(1);
    lastShownKeyRef.current = '';
    prevWrongRef.current = 0;
    interactionConsumedRef.current = false;
    setInteractionConsumed(false);
    armIdleTimer();
  }, [armIdleTimer, enabled]);

  const noteIncorrectAnswer = useCallback(() => {
    if (!enabled) return;
    noteUserAction();
    setWrongCount((w) => w + 1);
  }, [enabled, noteUserAction]);

  const requestHintManually = useCallback(() => {
    if (!enabled) return;
    noteUserAction();
    setOpen(true);
    setVisibleLevel((vl) => {
      const next = Math.min(maxLevel, Math.max(1, vl));
      trackMiniAppEvent.hintRequested(activityId, { level: next });
      trackMiniAppEvent.hintShown(activityId, {
        trigger: 'manual',
        level: next,
        phase: hasOneShot && !interactionConsumedRef.current ? 'interaction' : 'content',
      });
      return next;
    });
  }, [activityId, enabled, hasOneShot, maxLevel, noteUserAction]);

  const dismissOverlay = useCallback(() => {
    setOpen(false);
    setExplanationOpen(false);
    if (hasOneShot) {
      interactionConsumedRef.current = true;
      setInteractionConsumed(true);
    }
    noteUserAction();
  }, [hasOneShot, noteUserAction]);

  const anotherClue = useCallback(() => {
    noteUserAction();
    if (hasOneShot && !interactionConsumedRef.current) {
      interactionConsumedRef.current = true;
      setInteractionConsumed(true);
      setVisibleLevel(Math.min(maxLevel, Math.max(1, wrongCount)));
      trackMiniAppEvent.hintRequested(activityId, { action: 'another_clue', phase: 'after_interaction' });
      return;
    }
    setVisibleLevel((vl) => {
      const next = Math.min(maxLevel, vl + 1);
      trackMiniAppEvent.hintRequested(activityId, { action: 'another_clue', level: next });
      return next;
    });
  }, [activityId, hasOneShot, maxLevel, noteUserAction, wrongCount]);

  const openExplanation = useCallback(() => {
    if (!resolvedExplanation) return;
    noteUserAction();
    setExplanationOpen(true);
    trackMiniAppEvent.hintExplanationShown(activityId, { wrongCount });
  }, [activityId, noteUserAction, resolvedExplanation, wrongCount]);

  const isInteractionPhase = hasOneShot && !interactionConsumed;

  const hintEntry = useMemo(() => {
    if (isInteractionPhase) {
      return {
        text: oneShotInteractionHint.text.trim(),
        audioText: (oneShotInteractionHint.audioText || oneShotInteractionHint.text).trim(),
      };
    }
    return hintEntryAtLevel(normalized, visibleLevel);
  }, [isInteractionPhase, normalized, oneShotInteractionHint, visibleLevel]);

  const anotherClueEnabled = isInteractionPhase ? maxLevel > 0 : visibleLevel < maxLevel;

  const readAloudTrackLevel = isInteractionPhase ? 0 : visibleLevel;

  const showHowAvailable = wrongCount >= 3 && !!resolvedExplanation;

  /** Reserve space so bottom-anchored hint panels do not cover drop zones or tray pieces (desktop + mobile). */
  const contentReserveStyle = open
    ? {
        paddingBottom: isNarrow ? 'clamp(168px, 36vh, 300px)' : 'clamp(170px, 30vh, 280px)',
        boxSizing: 'border-box',
      }
    : undefined;

  return {
    isNarrow,
    open,
    visibleLevel,
    maxLevel,
    wrongCount,
    explanationOpen,
    showHowAvailable,
    hintEntry,
    resolvedExplanation,
    contentReserveStyle,
    isInteractionPhase,
    anotherClueEnabled,
    readAloudTrackLevel,
    noteUserAction,
    noteIncorrectAnswer,
    noteCorrectAnswer,
    resetSession,
    requestHintManually,
    dismissOverlay,
    anotherClue,
    openExplanation,
    setExplanationOpen,
    showHelpButton: enabled && !isActivityComplete,
  };
}
